import JsPdfExtended from './JsPdfExtended';
import HorizontalBarChart from './components/HorizontalBarChart';
import { normalizeConsolidationData } from './constants';
import globalDispatcher from '@/redux/global/actions';
import { Box } from '@material-ui/core';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';

/**
 * CR Consolidation Report PDF: 1 Font Cover + N pages (each item = one page, same UI).
 * Render a hidden Chart.js chart per item, export to image for each page.
 */
const CRConsolidationReportPDF = memo(
  ({
    consolidationData,
    corporateData,
    pdfStoreKey = 'pdfDocConsolidation',
    onPdfReady,
  }) => {
    const raw = consolidationData || corporateData || null;

    const { projectInfo, items } = useMemo(() => {
      const data =
        typeof normalizeConsolidationData === 'function'
          ? normalizeConsolidationData(raw)
          : raw;
      return {
        projectInfo: data?.projectInfo ?? null,
        items: Array.isArray(data?.items) ? data.items : [],
      };
    }, [raw]);

    const [chartsByIndex, setChartsByIndex] = useState({});
    const needsCharts = items?.some((it) => it?.chartData?.length > 0);
    const generatingRef = useRef(false);

    useEffect(() => {
      if (!projectInfo || !items?.length) return;
      if (needsCharts) {
        // Only wait for charts that actually exist (items with chartData)
        const requiredChartsCount = items.reduce(
          (sum, it) => sum + (it?.chartData?.length ? 1 : 0),
          0
        );
        const readyCount = Object.keys(chartsByIndex || {}).length;
        if (readyCount < requiredChartsCount) return;
      }

      // Prevent re-entrant generation (can happen when data references change)
      if (generatingRef.current) return;
      generatingRef.current = true;

      try {
        const _doc = new JsPdfExtended();
        _doc.addFontCover({
          projectInfo,
          items,
          coverTitle: 'Segmented Analysis Report',
        });
        items.forEach((item, i) => {
          const chartInstance = chartsByIndex?.[i] || null;
          _doc.addConsolidationPage(item, chartInstance);
        });
        _doc.addPagingAll();
        globalDispatcher.saveDataSuccess(pdfStoreKey, _doc);
        if (typeof onPdfReady === 'function') {
          onPdfReady(_doc);
        }
      } catch (e) {
        // Keep UI responsive even if PDF generation fails
        // eslint-disable-next-line no-console
        console.error('CRConsolidationReportPDF: failed to generate PDF', e);
      } finally {
        generatingRef.current = false;
      }
    }, [
      projectInfo,
      items,
      pdfStoreKey,
      chartsByIndex,
      needsCharts,
      onPdfReady,
    ]);

    useEffect(() => {
      // Reset when items truly change (memoized by raw)
      setChartsByIndex({});
    }, [raw]);

    const onDoneForIndex = (index) => (chart) => {
      if (!chart) return;
      setChartsByIndex((prev) => {
        if (prev && prev[index]) return prev;
        return { ...(prev || {}), [index]: chart };
      });
    };

    return (
      <>
        {items?.map((it, idx) =>
          it?.chartData?.length ? (
            <Box
              key={idx}
              position="absolute"
              left={-9999}
              top={0}
              width={632}
              height={280}
              visibility="hidden"
              aria-hidden
            >
              <HorizontalBarChart
                chartData={it.chartData}
                onDone={onDoneForIndex(idx)}
                animationDuration={0}
              />
            </Box>
          ) : null
        )}
      </>
    );
  }
);

CRConsolidationReportPDF.displayName = 'CRConsolidationReportPDF';

export default CRConsolidationReportPDF;
